import { SafeDictionary } from "@insite/client-framework/Common/Types";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { getOrders } from "@insite/client-framework/Services/OrderService";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = Handler<{
    ids: SafeDictionary<boolean>;
    type?:string;
}>;

export const DispatchBeginExportOrders: HandlerType = props => {
    props.dispatch({
        type: "Pages/OrderHistory/BeginExportOrders",
    });
};

export const getJsonErrorMsg = (failedMsg: string): string => {
    try {
        const parsed = JSON.parse(failedMsg)
            ?.map((item: { Message: any }) => item.Message)
            .join(", ");
        return parsed;
    } catch {
        return failedMsg;
    }
};

type OrderLineData = {
    partNumber: string;
    customerPartNumber: string;
    quantityOrdered: number;
    qtyShipped: number;
    qtyNoShip: number;
    orderDate: string;
    erpOrderNumber: string;
    orderType: string;
    webOrderNumber: string;
    customerPO: string;
    customerVatNumber: string;
    statusDisplay: string;
};

export const ExportData: HandlerType = async props => {
    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");

    let data: any[] = [];
    const state = props.getState();
    const language = state.context.session.language;
    const orderData = (order: OrderModel): OrderLineData[] => {        
      return(order.orderLines ?? []).map(line => ({        
            orderDate: order.orderDate
                ? getLocalizedDateTime({
                      dateTime: new Date(order.orderDate),
                      language,
                  })
                : "",
            erpOrderNumber: order.erpOrderNumber ?? "",
            orderType:
                order.properties?.isEmergencyOrder?.toLocaleLowerCase() === "true"
                    ? translate("Emergency Order")
                    : translate("Stock Order"),
            webOrderNumber: order.webOrderNumber ?? "",
            customerPO: order.customerPO ?? "",
            customerVatNumber: order.customerVatNumber ?? "",
            partNumber: line.productErpNumber ?? "",
            customerPartNumber: line.customerProductNumber ?? "",
            quantityOrdered: line.qtyOrdered ?? 0,
            qtyShipped: line.qtyShipped ?? 0,
            qtyNoShip: (line.qtyOrdered ?? 0) - (line.qtyShipped ?? 0),
            statusDisplay:line.status,
        }))||[];
        
    };  

    if (Object.keys(props.parameter.ids).length) {
        const ordersDataView = getOrdersDataView(state, state.pages.orderHistory.getOrdersParameter);
        if (ordersDataView.value) {
            ordersDataView.value.forEach(order => {
                if (props.parameter.ids[order.id]) {
                    data.push(orderData(order));
                }
            });
        }
    } else {
        const getOrdersApiParameternew ={  ...state.pages.orderHistory.getOrdersParameter};
        if(props.parameter.type==="IsOrderVerify")
        {
          getOrdersApiParameternew.additionalQueryStringParameters={"isorderverify": true}
        }
        const orderResult = await getOrders({
            ...getOrdersApiParameternew,
            page: 1,
            pageSize: 9999,
            expand:["orderLines"],
                        
        });
        if (props.parameter.type==="IsOrderVerify" && orderResult && orderResult.orders) {
            orderResult.orders.forEach(order => {               
                const rows =(order.orderLines ?? []).map(line => ({
                   customerNo: order.customerNumber,
                   customerName: order.btCompanyName,
                    processNo: order.webOrderNumber ?? "",
                    orderedDate: order.orderDate
                    ? getLocalizedDateTime({
                          dateTime: new Date(order.orderDate),
                          language,
                      })
                    : "",
                    orderType:
                    order.properties?.isEmergencyOrder?.toLocaleLowerCase() === "true"
                    ? translate("Emergency Order")
                    : order.properties?.isAnnualReturnsOrder?.toLocaleLowerCase() === "true"
                    ? translate("Annual Returns Order")
                    : order.properties?.isStandardReturnsOrder?.toLocaleLowerCase() === "true"
                    ? translate("Standard Returns Order") 
                    :translate("Stock Order"),
                    userId: order.terms ?? "",
                    primaryPONo: order.customerPO ?? "",
                    secondaryPONo: order.customerVatNumber ?? "",
                    partNo: line.productErpNumber ?? "",  
                    reason:order.properties?.failedOrdMsg!=="" && order.properties?.failedOrdMsg!==undefined ? getJsonErrorMsg(order.properties?.failedOrdMsg) :"" ,             
                    
                }))||[];
                for (const row of rows) {
                    data.push(row);
                }                
            });
        }else if (orderResult && orderResult.orders) {
            orderResult.orders.forEach(order => {               
                const rows =(order.orderLines ?? []).map(line => ({
                    orderDate: order.orderDate
                        ? getLocalizedDateTime({
                              dateTime: new Date(order.orderDate),
                              language,
                          })
                        : "",
                    erpOrderNumber: order.erpOrderNumber ?? "",
                    orderType:
                        order.properties?.isEmergencyOrder?.toLocaleLowerCase() === "true"
                            ? translate("Emergency Order")
                            : translate("Stock Order"),
                    webOrderNumber: order.webOrderNumber ?? "",
                    customerPO: order.customerPO ?? "",
                    customerVatNumber: order.customerVatNumber ?? "",
                    partNumber: line.productErpNumber ?? "",
                    customerPartNumber: line.customerProductNumber ?? "",
                    quantityOrdered: line.qtyOrdered ?? 0,
                    qtyShipped: line.qtyShipped ?? 0,
                    qtyNoShip: (line.qtyOrdered ?? 0) - (line.qtyShipped ?? 0),
                    requestdate: line.customerSequence,
                    status:line.status,
                    dateShipped:line.lastShipDate ,
                    carrier:line.customerNumber ,
                    tracking: line.lineType,
                    invoiceDate: line.description,
                    invoiceNumber:line.notes,
                }))||[];
                for (const row of rows) {
                    data.push(row);
                }                
            });
        }
    }

   

    const documentData = {
        fields: [
            translate("Order Date"),
            translate("Order Number"),
            translate("Order Type"),
            translate("Process Number"),
            translate("Primary PO Number"),
            translate("Secondary PO Number"),
            translate("Dana Part Number"),
            translate("Customer Part Number"),
            translate("Quantity Ordered"),
            translate("Quantity Shipped"),
            translate("Quantity Not Shipped"),
            translate("Line Item Status"),
            translate("Ship Date"),
            translate("Carrier"),
            translate("Tracking Number"),
            translate("Invoice Date"),
            translate("Invoice Number"),
        ],
        data,
    };

    const csv = unparse(data);   
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });

    const fileName = `order_table.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

export const DispatchCompleteExportOrders: HandlerType = props => {
    props.dispatch({
        type: "Pages/OrderHistory/CompleteExportOrders",
    });
};

export const chain = [DispatchBeginExportOrders, ExportData, DispatchCompleteExportOrders];

const exportOrders = createHandlerChainRunner(chain, "ExportOrders");
export default exportOrders;
